import React from 'react';
import { MapPin, Tag } from 'lucide-react';

const FoodCard = ({ food, onClick, onEdit }) => {
  const isFreeFood = food.protein_exchanges < 1.0;
  
  // Extract location and type labels from the food object
  const locationLabels = food.locations || [];
  const typeLabels = food.types || [];

  // Handle missing food data gracefully
  if (!food) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-gray-500">No food data available</p>
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={() => onClick(food)}
    >
      <div className="p-6">
        <div className="flex items-start space-x-6">
          {/* Image Placeholder */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
              {food.photo_urls && food.photo_urls.length > 0 ? (
                <img 
                  src={food.photo_urls[0]} 
                  alt={food.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
              ) : (
                <div className="text-gray-400 text-sm text-center">
                  <div>📷</div>
                  <div>No Image</div>
                </div>
              )}
            </div>
          </div>

          {/* Food Information */}
          <div className="flex-1 min-w-0">
            {/* Brand */}
            {food.brand && (
              <p className="text-sm text-gray-500 mb-1">{food.brand}</p>
            )}
            
            {/* Food Name */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {food.name || 'Unnamed Food'}
            </h3>
            
            {/* Product Size */}
            <p className="text-sm text-gray-600 mb-2 bg-gray-50 px-2 py-1 rounded">
              <span className="font-semibold text-gray-800">Serving Size:</span> {food.serving_size || 'Not specified'}
            </p>

            {/* Protein per 100g */}
            <p className="text-sm text-gray-600 mb-2 bg-gray-50 px-2 py-1 rounded">
              <span className="font-semibold text-gray-800">Protein per 100g:</span> {food.protein_per_100g !== undefined && food.protein_per_100g !== null ? `${food.protein_per_100g}g` : 'Not specified'}
            </p>

            {/* Location and Type Labels */}
            <div className="flex flex-wrap gap-2 mb-2">
              {locationLabels.length > 0 ? (
                locationLabels.map((location, index) => (
                  <span 
                    key={`location-${index}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: location.color ? `${location.color}20` : '#dbeafe',
                      color: location.color || '#1e40af',
                      border: location.color ? `1px solid ${location.color}40` : '1px solid #93c5fd'
                    }}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    {location.name || 'Unknown Location'}
                  </span>
                ))
              ) : null}
              {typeLabels.length > 0 ? (
                typeLabels.map((type, index) => (
                  <span 
                    key={`type-${index}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: type.color ? `${type.color}20` : '#dcfce7',
                      color: type.color || '#166534',
                      border: type.color ? `1px solid ${type.color}40` : '1px solid #86efac'
                    }}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {type.name || 'Unknown Type'}
                  </span>
                ))
              ) : null}
            </div>

            {/* MINIMAL VERSION - Just show the value that's causing the "0" */}
            <div className="border-2 border-red-500 p-2 mb-2">
              <div className="text-xs text-red-600 font-bold">🔍 DEBUGGING THE "0"</div>
              <div className="text-xs text-gray-600">contains_aspartame: {JSON.stringify(food.contains_aspartame)}</div>
              <div className="text-xs text-gray-600">protein_exchanges: {JSON.stringify(food.protein_exchanges)}</div>
              <div className="text-xs text-gray-600">protein_per_100g: {JSON.stringify(food.protein_per_100g)}</div>
              <div className="text-xs text-gray-600">serving_size: {JSON.stringify(food.serving_size)}</div>
            </div>

            {/* Additional Info */}
            <div className="border-2 border-blue-500 p-1 mb-2">
              <div className="text-xs text-blue-600 font-bold">🔍 ADDITIONAL INFO SECTION START</div>
              {food.contains_aspartame && (
                <div className="mb-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-800 text-white border border-red-900">
                    ⚠️ Contains Aspartame
                  </span>
                </div>
              )}
              <div className="text-xs text-blue-600 font-bold">🔍 ADDITIONAL INFO SECTION END</div>
            </div>
          </div>

          {/* Exchange Circle */}
          <div className="flex-shrink-0">
            <div 
              className={`w-20 h-20 rounded-full flex flex-col items-center justify-center text-center ${
                isFreeFood 
                  ? 'bg-green-100 border-2 border-green-300' 
                  : 'bg-blue-100 border-2 border-blue-300'
              }`}
            >
              <span className={`text-xl font-bold ${
                isFreeFood ? 'text-green-800' : 'text-blue-800'
              }`}>
                {food.protein_exchanges !== undefined ? food.protein_exchanges : 0}
              </span>
              <span className={`text-sm ${
                isFreeFood ? 'text-green-600' : 'text-blue-600'
              }`}>
                exchanges
              </span>
            </div>
          </div>
        </div>

        {/* Edit Button */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(food);
            }}
            className="text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
          >
            ✏️ Edit
          </button>
        </div>
      </div>
    </div>
  );
};

export default FoodCard;
